import type { NormalizedOptions } from 'ky';
import { HTTPError } from 'ky';
import { z } from 'zod/v4';
import type { $ZodIssue } from 'zod/v4/core';

export const tempoApiErrorSchema = z.union([
  z.object({
    title: z.string().optional(),
    detail: z.string().optional(),
    status: z.number(),
    code: z.string(),
    meta: z.record(z.string(), z.any()).optional(),
  }),
  z
    .object({
      message: z.string().optional(),
      details: z.string().array().optional(),
      status: z.number(),
    })
    .transform((data) => ({
      message: data.message,
      title: data.message,
      detail: data?.details?.[0] || '',
      status: data.status,
      code: 'UNKNOWN_ERROR',
    })),
]);

export const tempoApiErrorWrapperSchema = z.union([
  z.object({ errors: z.array(tempoApiErrorSchema) }).transform((data) => data.errors[0]),
  z.array(tempoApiErrorSchema).transform((data) => data[0]),

  // errors that come from NodeJS detailsZValidatorCb
  tempoApiErrorSchema,
  z
    .object({ message: z.string(), status: z.number(), details: z.array(z.custom<$ZodIssue>()) })
    .transform((data) => ({
      status: data.status,
      code: data.details[0]?.code || 'UNKNOWN_ERROR',
      title: data.message,
      detail: data.details
        .map((detail) => `${detail.path.join('.')} - ${detail.message}`)
        .join('; '),
    })),

  z.object({ message: z.string(), status: z.number() }).transform((data) => ({
    status: data.status,
    code: 'UNKNOWN_ERROR',
    title: data.message,
    detail: data.message,
  })),
]);

export type TempoApi = z.infer<typeof tempoApiErrorSchema>;

export class TempoApiError extends HTTPError {
  public error: TempoApi;

  constructor(error: TempoApi, response: Response, request: Request) {
    const options: Partial<NormalizedOptions> = {
      credentials: 'same-origin',
      retry: { limit: 0 },
      prefixUrl: '',
    };

    super(response, request, options as NormalizedOptions);
    this.name = 'TempoApiError';
    this.error = error;
    this.message = error.title || 'API Error';

    Object.setPrototypeOf(this, TempoApiError.prototype);
  }
}
